
import React, { useState } from 'react';

interface Vehicle {
  id: number;
  year: number;
  make: string;
  model: string;
  trim: string;
  mileage: number;
  transmission: string;
  drivetrain: string;
  exteriorColor: string;
  condition: string;
  estimatedValue: number;
  location: string;
  postedDate: string;
  images: string[];
  owner: {
    name: string;
    verified: boolean;
    avatar?: string;          // <-- added optional avatar
  };
}

interface VehiclesGridProps {
  vehicles: Vehicle[];
  viewMode: 'grid' | 'list';
}

export default function VehiclesGrid({ vehicles, viewMode }: VehiclesGridProps) {
  const [compareList, setCompareList] = useState<number[]>([]);
  const [showCompareBar, setShowCompareBar] = useState(false);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatMileage = (mileage: number) => {
    return new Intl.NumberFormat('en-US').format(mileage);
  };

  const toggleCompare = (vehicleId: number) => {
    setCompareList(prev => {
      if (prev.includes(vehicleId)) {
        const newList = prev.filter(id => id !== vehicleId);
        if (newList.length === 0) {
          setShowCompareBar(false);
        }
        return newList;
      } else {
        if (prev.length >= 4) {
          alert('You can compare up to 4 vehicles at a time');
          return prev;
        }
        setShowCompareBar(true);
        return [...prev, vehicleId];
      }
    });
  };

  const handleCompare = () => {
    if (compareList.length < 2) {
      alert('Please select at least 2 vehicles to compare');
      return;
    }
    // Fixed the unterminated string literal
    window.location.href = `/compare-vehicles?ids=${compareList.join(',')}`;
  };

  const clearCompare = () => {
    setCompareList([]);
    setShowCompareBar(false);
  };

  if (vehicles.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-12 text-center">
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="ri-car-line text-4xl text-gray-400"></i>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">No vehicles found</h3>
        <p className="text-gray-600 mb-6">
          Try adjusting your filters or search criteria to find more vehicles
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-semibold whitespace-nowrap cursor-pointer"
        >
          Reset Filters
        </button>
      </div>
    );
  }

  if (viewMode === 'list') {
    return (
      <div className="space-y-4">
        {vehicles.map((vehicle) => (
          <a
            key={vehicle.id}
            href={`/vehicle/${vehicle.id}`}
            className="block bg-white rounded-xl border border-gray-200 hover:border-teal-500 hover:shadow-lg transition-all overflow-hidden group"
          >
            <div className="flex gap-6 p-5">
              {/* Image */}
              <div className="w-72 h-48 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                <img
                  src={vehicle.images?.[0] || '/placeholder.png'}
                  alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>

              {/* Content */}
              <div className="flex-1 flex flex-col">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1 group-hover:text-teal-600 transition-colors">
                      {vehicle.year} {vehicle.make} {vehicle.model}
                    </h3>
                    <p className="text-sm text-gray-600">{vehicle.trim}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-teal-600">{formatPrice(vehicle.estimatedValue)}</p>
                    <p className="text-xs text-gray-500 mt-1">Estimated Value</p>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg">
                      <i className="ri-dashboard-line text-gray-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Mileage</p>
                      <p className="text-sm font-semibold text-gray-900">{formatMileage(vehicle.mileage)}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg">
                      <i className="ri-settings-3-line text-gray-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Transmission</p>
                      <p className="text-sm font-semibold text-gray-900">{vehicle.transmission}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg">
                      <i className="ri-steering-2-line text-gray-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Drivetrain</p>
                      <p className="text-sm font-semibold text-gray-900">{vehicle.drivetrain}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg">
                      <i className="ri-paint-brush-line text-gray-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Color</p>
                      <p className="text-sm font-semibold text-gray-900">{vehicle.exteriorColor.split(' ')[0]}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-4">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-teal-50 text-teal-700 text-xs font-medium rounded-full">
                      <i className="ri-checkbox-circle-fill"></i>
                      {vehicle.condition}
                    </span>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-map-pin-line"></i>
                      {vehicle.location}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <i className="ri-time-line"></i>
                    {vehicle.postedDate}
                  </div>
                </div>
              </div>
            </div>
          </a>
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {vehicles.map((vehicle) => (
          <div
            key={vehicle.id}
            className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 group relative"
          >
            {/* Compare Checkbox */}
            <div className="absolute top-3 left-3 z-10">
              <label className="flex items-center gap-2 bg-white/95 backdrop-blur-sm px-3 py-2 rounded-lg shadow-md cursor-pointer hover:bg-white transition-colors">
                <input
                  type="checkbox"
                  checked={compareList.includes(vehicle.id)}
                  onChange={() => toggleCompare(vehicle.id)}
                  className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                />
                <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">Compare</span>
              </label>
            </div>

            {/* Image */}
            <a href={`/vehicle/${vehicle.id}`} className="block">
              <div className="relative w-full h-56 bg-gray-100 overflow-hidden">
                <img
                  src={vehicle.images?.[0] || '/placeholder.png'}
                  alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute top-3 right-3 bg-teal-600 text-white px-3 py-1.5 rounded-lg text-sm font-bold shadow-lg">
                  {formatPrice(vehicle.estimatedValue)}
                </div>
              </div>
            </a>

            {/* Content */}
            <div className="p-5">
              <a href={`/vehicle/${vehicle.id}`} className="block mb-3">
                <h3 className="text-xl font-bold text-gray-900 mb-1 group-hover:text-teal-600 transition-colors">
                  {vehicle.year} {vehicle.make}
                </h3>
                <p className="text-base text-gray-600">{vehicle.model}</p>
                <p className="text-sm text-gray-500">{vehicle.trim}</p>
              </a>

              {/* Specs */}
              <div className="grid grid-cols-2 gap-3 mb-4 pb-4 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg">
                    <i className="ri-speed-line text-gray-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Mileage</p>
                    <p className="text-sm font-semibold text-gray-900">{formatMileage(vehicle.mileage)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg">
                    <i className="ri-settings-3-line text-gray-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Transmission</p>
                    <p className="text-sm font-semibold text-gray-900">{vehicle.transmission}</p>
                  </div>
                </div>
              </div>

              {/* Location & Owner */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <i className="ri-map-pin-line"></i>
                  <span>{vehicle.location}</span>
                </div>
                <div className="flex items-center gap-2">
                  <img
                    src={vehicle.owner.avatar || '/default-avatar.png'}
                    alt={vehicle.owner.name}
                    className="w-7 h-7 rounded-full object-cover border-2 border-gray-200"
                  />
                  <span className="text-sm font-semibold text-gray-700">{vehicle.owner.name}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <a
                  href={`/vehicle/${vehicle.id}`}
                  className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-semibold text-center whitespace-nowrap cursor-pointer"
                >
                  View Details
                </a>
                <button className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-semibold whitespace-nowrap cursor-pointer">
                  <i className="ri-heart-line"></i>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Compare Bar */}
      {showCompareBar && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-teal-600 shadow-2xl z-40 animate-slide-up">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-lg">
                  <i className="ri-scales-3-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">
                    {compareList.length} Vehicle{compareList.length > 1 ? 's' : ''} Selected
                  </h3>
                  <p className="text-sm text-gray-600">
                    {compareList.length < 2
                      ? 'Select at least 2 vehicles to compare'
                      : `Ready to compare • ${4 - compareList.length} slot${4 - compareList.length !== 1 ? 's' : ''} remaining`}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={clearCompare}
                  className="px-5 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-semibold whitespace-nowrap cursor-pointer"
                >
                  Clear All
                </button>
                <button
                  onClick={handleCompare}
                  disabled={compareList.length < 2}
                  className={`px-6 py-3 rounded-lg font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                    compareList.length >= 2
                      ? 'bg-teal-600 text-white hover:bg-teal-700'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Compare Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
